import { FC } from "react"
import { useFormik } from "formik"
import Modal from "@/components/Modal"
import TextField from "@/components/TextField"
import Button from "@/components/Button"
import OutlineButton from "@/components/OutlineButton"
import validationSchema from "../../utils/validationSchema"
import FormFieldErrorMessage from "@/components/FormFieldErrorMessage"
import { useEditOrganization } from "../../hooks/useEditOrganization"

type Props = {
  open: boolean
  onClose: () => void
  organizationId: string
  initialName: string
  initialUrl: string
  initialCatchAll?: string
}

const EditOrganizationModal: FC<Props> = ({
  organizationId,
  open,
  onClose,
  initialName,
  initialUrl,
  initialCatchAll = "",
}: Props) => {
  const { editOrganization, response } = useEditOrganization()
  const {
    handleSubmit,
    getFieldProps,
    errors: { name: nameError, url: urlError, catchAll: catchAllError },
    touched: { name: nameTouched, url: urlTouched, catchAll: catchAllTouched },
    resetForm,
  } = useFormik({
    initialValues: {
      name: initialName,
      url: initialUrl,
      catchAll: initialCatchAll,
    },
    validationSchema,
    onSubmit: async ({ name, url, catchAll }) => {
      try {
        await editOrganization({ id: organizationId, name, url, catchAll })
        handleClose()
      } catch (e) {
        console.error(e)
      }
    },
  })
  const handleClose = () => {
    resetForm()
    onClose && onClose()
  }
  return (
    <Modal
      open={open}
      title="Edit Organization"
      actions={
        <div className="flex gap-4">
          <OutlineButton
            onClick={handleClose}
            className="text-black btn-sm min-w-[86px]"
            type="button"
          >
            Cancel
          </OutlineButton>
          <Button
            disabled={response.isLoading}
            className="btn-sm min-w-[86px]"
            type="submit"
          >
            Save
          </Button>
        </div>
      }
      onClose={handleClose}
      onSubmit={handleSubmit}
      contentClassName="space-y-3"
    >
      <TextField
        type="text"
        topLeftLabel="Name"
        placeholder="Enter organization name"
        rootClassName="[&_label.label]:pt-0"
        className="input-sm"
        autoComplete="off"
        bottomLeftLabel={
          <FormFieldErrorMessage error={nameError} touched={nameTouched} />
        }
        {...getFieldProps("name")}
      />
      <TextField
        type="url"
        topLeftLabel="Website"
        placeholder="Enter organization URL"
        className="input-sm"
        bottomLeftLabel={
          <FormFieldErrorMessage error={urlError} touched={urlTouched} />
        }
        {...getFieldProps("url")}
      />
      <TextField
        type="email"
        autoComplete="new-password"
        topLeftLabel="Catch-All Email"
        placeholder="Enter catch-all email"
        className="input-sm"
        bottomLeftLabel={
          <FormFieldErrorMessage
            error={catchAllError}
            touched={catchAllTouched}
          />
        }
        {...getFieldProps("catchAll")}
      />
    </Modal>
  )
}

export default EditOrganizationModal
